import React, { useEffect, useState, useLayoutEffect, useCallback } from "react";
import { X, ArrowRight, Sparkles } from "lucide-react";

/**
 * GuidedTour — lightweight spotlight tour that anchors floating cards to
 * elements identified by `data-testid`. Zero external deps.
 *
 * Props:
 *   steps   — array of { selector, title, body }
 *   open    — bool
 *   onClose — fn()
 *
 * Persists "seen" state via localStorage key `venturaz.tour_seen` so a
 * founder isn't shown twice unless they explicitly reopen.
 */
export default function GuidedTour({ steps = [], open, onClose, storageKey = "venturaz.tour_seen" }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const step = steps[idx];

  const measure = useCallback(() => {
    if (!step) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.selector}"]`);
    if (!el) { setRect(null); return; }
    // Scroll into view centered.
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const r = el.getBoundingClientRect();
    // Container is `fixed inset-0` so coordinates are viewport-relative; do
    // NOT add scrollY (that double-offsets once the page scrolls).
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [step]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    // Re-measure after scroll settles.
    const t = setTimeout(measure, 350);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, idx, measure]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, idx]);

  useEffect(() => { if (!open) setIdx(0); }, [open]);

  if (!open) return null;

  const close = () => {
    try { localStorage.setItem(storageKey, "1"); } catch (e) { /* ignore */ }
    onClose?.();
  };
  const next = () => {
    if (idx >= steps.length - 1) close();
    else setIdx((i) => i + 1);
  };
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  // Card position: prefer below the highlighted element; if not enough
  // space, go above.
  const CARD_W = 340;
  const CARD_H_ESTIMATE = 220;
  let cardStyle = null;
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const below = spaceBelow > CARD_H_ESTIMATE + 30;
    const top = below
      ? rect.top + rect.height + 14
      : rect.top - CARD_H_ESTIMATE - 14;
    // Clamp horizontally
    let left = rect.left + rect.width / 2 - CARD_W / 2;
    if (left < 12) left = 12;
    if (left + CARD_W > window.innerWidth - 12) left = window.innerWidth - CARD_W - 12;
    cardStyle = { top, left, width: CARD_W };
  }

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="guided-tour">
      {/* Dark backdrop w/ transparent cutout via inset shadow trick */}
      <div className="absolute inset-0 bg-black/55 pointer-events-auto" onClick={close} />

      {/* Cutout ring around the anchor element */}
      {rect && (
        <div
          className="absolute rounded-lg pointer-events-none transition-all duration-300 ease-out"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55), 0 0 0 3px #10B981",
            borderRadius: 12,
          }}
          data-testid="guided-tour-spotlight"
        />
      )}

      {/* Floating card */}
      {cardStyle && (
        <div
          className="absolute rounded-lg bg-white shadow-2xl border border-emerald-200 pointer-events-auto"
          style={cardStyle}
          data-testid="guided-tour-card"
        >
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <Sparkles size={12} className="text-emerald-600" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">
                  Step {idx + 1} of {steps.length}
                </div>
              </div>
              <button
                onClick={close}
                className="text-ink-muted hover:text-ink"
                aria-label="Close tour"
                data-testid="guided-tour-close"
              ><X size={16} /></button>
            </div>
            <div className="font-heading text-ink text-lg mb-1.5" data-testid="guided-tour-title">
              {step.title}
            </div>
            <div className="text-sm text-ink-soft leading-relaxed mb-4" data-testid="guided-tour-body">
              {step.body}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {steps.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 rounded-full transition-all ${i === idx ? "w-6 bg-emerald-600" : "w-1 bg-navy/15"}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                {idx > 0 && (
                  <button
                    onClick={prev}
                    className="text-xs text-ink-muted hover:text-ink px-2 py-1"
                    data-testid="guided-tour-prev"
                  >Back</button>
                )}
                <button
                  onClick={next}
                  className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading inline-flex items-center gap-1"
                  data-testid="guided-tour-next"
                >
                  {idx === steps.length - 1 ? "Done" : "Next"}
                  {idx !== steps.length - 1 && <ArrowRight size={12} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
